import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ArchThumb,
  BottomSheet,
  Button,
  Card,
  DaysLeftBadge,
  Icon,
  iconChevron,
  List,
  ListItem,
  Notice,
  Skeleton,
  useSnackbar,
} from '@sauda/ui/kit';
import { useChrome } from '../../app/chrome.js';
import { api } from '../../lib/api.js';
import {
  thingsToday,
  type Attention,
  type TodayData,
  type TodayVisit,
} from '../../lib/dashboard.js';
import { canCall, nextStepLabel, telHref, type NextStep } from '../../lib/next-step.js';
import { formatRupees } from '../../lib/money.js';
import { whatsappLink } from '../../lib/share-text.js';
import { formatCalendarDate } from '../../lib/time.js';

/** A card with its own loading, empty and error line, so one failed request never blanks the screen. */
export function SectionCard<T>({
  title,
  query,
  count,
  empty,
  quiet = false,
  children,
}: {
  title: string;
  query: UseQueryResult<T>;
  count?: number;
  empty?: ReactNode;
  /** Draw nothing at all when there is nothing to show. */
  quiet?: boolean;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (quiet && query.isSuccess && count === 0) return null;
  return (
    <section aria-label={title === '' ? undefined : title} className="today-section">
      <div className="card-head" hidden={title === ''}>
        <h2 className="k-t-title-sm">{title}</h2>
        {count === undefined || count === 0 ? null : (
          <span className="card-head__count k-t-label-sm" aria-label={t('today.count', { count })}>
            {count}
          </span>
        )}
      </div>
      {query.isPending ? (
        <Skeleton height={72} />
      ) : query.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('today.loadFailed')}
        </Notice>
      ) : query.data !== undefined && count === 0 && empty !== undefined ? (
        <Card>{empty}</Card>
      ) : query.data !== undefined ? (
        children(query.data)
      ) : null}
    </section>
  );
}

export function useTodayQuery(enabled: boolean, agencyId: string | null) {
  return useQuery({
    queryKey: ['today', agencyId],
    queryFn: () => api<TodayData>('/dashboard/today'),
    enabled,
  });
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

function useVisitAction() {
  const cache = useQueryClient();
  const snack = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (v: { id: string; action: 'confirm' | 'done' | 'no-show' }) =>
      api(`/visits/${v.id}/${v.action}`, { method: 'POST', body: {} }),
    onSuccess: async (_r, v) => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['today'] }),
        cache.invalidateQueries({ queryKey: ['attention'] }),
        cache.invalidateQueries({ queryKey: ['visits'] }),
      ]);
      if (v.action === 'done') {
        snack({
          text: t('today.visitDone'),
          action: {
            label: t('today.startDeal'),
            onClick: () => {
              void api<{ id?: string; dealId?: string }>('/deals', {
                method: 'POST',
                body: { visitId: v.id },
              }).then((d) => navigate(`/deals/${d.id ?? d.dealId ?? ''}`));
            },
          },
          duration: 8000,
        });
      } else if (v.action === 'confirm') snack({ text: t('today.visitConfirmed') });
    },
    onError: () => snack({ text: t('today.actionFailed') }),
  });
}

function VisitRow({
  v,
  action,
  label,
}: {
  v: TodayVisit;
  action: 'confirm' | 'done';
  label: string;
}) {
  const { t } = useTranslation();
  const chrome = useChrome();
  const run = useVisitAction();
  return (
    <ListItem
      title={v.clientName}
      supporting={`${v.propertyTitle} · ${v.slot === 'call_me' ? t('today.callMe') : v.slot}`}
      leading={
        <span className="row-avatar k-t-label" aria-hidden="true">
          {initials(v.clientName)}
        </span>
      }
      trailing={
        <Button
          size="md"
          loading={run.isPending}
          disabled={chrome.readOnly}
          onClick={() => run.mutate({ id: v.id, action })}
        >
          {label}
        </Button>
      }
    />
  );
}

export function RequestsCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('today.newRequests')}
      query={query}
      count={query.data?.visits.requested ?? 0}
      empty={<EmptyShare text={t('today.noRequests')} />}
    >
      {(d) => (
        <List label={t('today.newRequests')}>
          {d.newRequests.map((v) => (
            <VisitRow key={v.id} v={v} action="confirm" label={t('today.confirm')} />
          ))}
        </List>
      )}
    </SectionCard>
  );
}

export function VisitsTodayCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('today.todaysVisits')}
      query={query}
      count={query.data?.visits.confirmed ?? 0}
      quiet
      empty={<p className="k-t-body k-muted">{t('today.noVisits')}</p>}
    >
      {(d) => (
        <List label={t('today.todaysVisits')}>
          {d.todaysVisits.map((v) => (
            <VisitRow key={v.id} v={v} action="done" label={t('today.done')} />
          ))}
        </List>
      )}
    </SectionCard>
  );
}

function EmptyShare({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div className="today-empty">
      <p className="k-t-body k-muted">{text}</p>
      <Link className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg" to="/properties">
        {t('today.share')}
      </Link>
      <Link className="k-btn k-btn--text k-btn--md k-layer k-t-label-lg" to="/help">
        {t('help.firstTime')}
      </Link>
    </div>
  );
}

export function DealsCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <SectionCard
      title={t('today.dealsNeedYou')}
      query={query}
      count={query.data?.deals.needingAction ?? 0}
      empty={<p className="k-t-body k-muted">{t('today.noDeals')}</p>}
    >
      {(d) => (
        <List label={t('today.dealsNeedYou')}>
          {d.deals.items.map((deal) => {
            const label =
              deal.nextStep === null
                ? null
                : nextStepLabel(deal.nextStep as NextStep, {
                    signed: deal.memoSigned,
                    total: deal.memoSigners || 3,
                  });
            return (
              <ListItem
                key={deal.id}
                title={deal.propertyTitle}
                leading={<ArchThumb seed={deal.id} />}
                supporting={deal.clientName}
                onClick={() => void navigate(`/deals/${deal.id}`)}
                action={
                  label === null ? undefined : (
                    <Button variant="tonal" onClick={() => void navigate(`/deals/${deal.id}`)}>
                      {t(label.key, label.values)}
                    </Button>
                  )
                }
              />
            );
          })}
        </List>
      )}
    </SectionCard>
  );
}

export function RenewalsCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('today.renewalsSoon')}
      query={query}
      count={query.data?.renewals.dueSoon ?? 0}
      quiet
      empty={<p className="k-t-body k-muted">{t('today.noRenewals')}</p>}
    >
      {(d) => (
        <List label={t('today.renewalsSoon')}>
          {d.renewals.items.map((r) => (
            <ListItem
              key={r.id}
              title={r.propertyTitle}
              supporting={t(r.daysLeft < 0 ? 'renewals.dueLineLate' : 'renewals.dueLine', {
                date: formatCalendarDate(r.dueOn),
                days: Math.abs(r.daysLeft),
              })}
              leading={
                <DaysLeftBadge
                  days={r.daysLeft}
                  unit={r.daysLeft < 0 ? t('renewals.lateUnit') : undefined}
                  label={
                    r.daysLeft < 0
                      ? t('renewals.overdueDays', { count: -r.daysLeft })
                      : t('today.daysLeft', { count: r.daysLeft })
                  }
                />
              }
              trailing={
                canCall(r.tenantPhone) ? (
                  <a
                    className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                    href={telHref(r.tenantPhone)}
                  >
                    {t('today.call')}
                  </a>
                ) : (
                  <Link className="k-btn k-btn--text k-btn--md k-layer k-t-label-lg" to="/renewals">
                    {t('today.open')}
                  </Link>
                )
              }
            />
          ))}
        </List>
      )}
    </SectionCard>
  );
}

/** Enquiries typed in by hand whose follow up is due: one tap to call or message. */
export function FollowUpsCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('enquiry.followUps')}
      query={query}
      count={query.data?.enquiries.due ?? 0}
      quiet
    >
      {(d) => (
        <List label={t('enquiry.followUps')}>
          {d.enquiries.items.map((e) => (
            <ListItem
              key={e.id}
              title={e.name}
              supporting={[e.propertyTitle, e.note].filter(Boolean).join(' · ')}
              leading={
                <span className="row-avatar k-t-label" aria-hidden="true">
                  {initials(e.name)}
                </span>
              }
              action={
                e.phone === null ? undefined : (
                  <div className="chips-row">
                    <a
                      className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                      href={telHref(e.phone)}
                    >
                      {t('today.call')}
                    </a>
                    <a
                      className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                      href={whatsappLink('', e.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('visits.whatsapp')}
                    </a>
                  </div>
                )
              }
            />
          ))}
        </List>
      )}
    </SectionCard>
  );
}

export function BrokerageDueCard({ query }: { query: UseQueryResult<TodayData> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <SectionCard
      title={t('today.brokerageThisWeek')}
      query={query}
      count={query.data?.brokerage.dueSoon ?? 0}
      quiet
    >
      {(d) => (
        <List label={t('today.brokerageThisWeek')}>
          {d.brokerage.items.map((b) => (
            <ListItem
              key={b.id}
              title={b.propertyTitle}
              supporting={t(b.daysLeft < 0 ? 'today.brokerageLate' : 'today.brokerageIn', {
                amount: formatRupees(b.balancePaise),
                date: formatCalendarDate(b.dueOn),
                count: Math.abs(b.daysLeft),
              })}
              trailing={<Icon icon={iconChevron} />}
              onClick={() => void navigate(`/deals/${b.id}`)}
            />
          ))}
        </List>
      )}
    </SectionCard>
  );
}

/** The one line that says all is well, drawn only when no list above has anything in it. */
export function AllClear({
  today,
  unassigned,
  waiting,
  ready,
}: {
  today: TodayData | undefined;
  unassigned: number;
  waiting: number;
  ready: boolean;
}) {
  const { t } = useTranslation();
  if (today === undefined || !ready) return null;
  const things = thingsToday(today);
  if (things.total + unassigned + waiting > 0) return null;
  return (
    <Card>
      <p className="k-t-body k-muted">{t('today.nothingDue')}</p>
    </Card>
  );
}

export function MoneyCard({
  received,
  due,
  overdue,
}: {
  received: number;
  due: number;
  overdue: number;
}) {
  const { t } = useTranslation();
  return (
    <section aria-label={t('today.myMoney')} className="today-section">
      <h2 className="k-t-title-sm">{t('today.myMoney')}</h2>
      <Card>
        <div className="stat-grid">
          <div>
            <p className="k-t-label-sm k-muted">{t('today.received')}</p>
            <p className="k-t-money">{formatRupees(received)}</p>
          </div>
          <div>
            <p className="k-t-label-sm k-muted">{t('today.due')}</p>
            <p className="k-t-money">{formatRupees(due)}</p>
          </div>
        </div>
        {overdue > 0 ? (
          <p className="k-t-body k-error">
            {t('today.overdueLine', { amount: formatRupees(overdue) })}
          </p>
        ) : null}
      </Card>
    </section>
  );
}

/** A manager or owner assigns an unassigned visit from a short list of people. */
export function UnassignedCard({ query }: { query: UseQueryResult<Attention> }) {
  const { t } = useTranslation();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [open, setOpen] = useState<string | null>(null);
  const members = useQuery({
    queryKey: ['assignable'],
    queryFn: () => api<{ id: string; name: string | null; role: string }[]>('/visits/assignable'),
    enabled: open !== null,
  });
  const assign = useMutation({
    mutationFn: (v: { visit: string; member: string }) =>
      api(`/visits/${v.visit}/assign`, { method: 'POST', body: { memberId: v.member } }),
    onSuccess: async () => {
      setOpen(null);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['attention'] }),
        cache.invalidateQueries({ queryKey: ['today'] }),
      ]);
      snack({ text: t('today.assigned') });
    },
    onError: () => snack({ text: t('today.actionFailed') }),
  });
  return (
    <>
      <SectionCard
        title={t('today.unassigned')}
        query={query}
        count={query.data?.unassignedVisits.count ?? 0}
        empty={<p className="k-t-body k-muted">{t('today.noUnassigned')}</p>}
      >
        {(d) => (
          <List label={t('today.unassigned')}>
            {d.unassignedVisits.items.map((i) => (
              <ListItem
                key={i.id}
                title={i.propertyTitle}
                supporting={t('today.waitingDays', { count: i.daysLate })}
                trailing={
                  <Button size="md" disabled={chrome.readOnly} onClick={() => setOpen(i.id)}>
                    {t('today.assign')}
                  </Button>
                }
              />
            ))}
          </List>
        )}
      </SectionCard>
      <BottomSheet
        open={open !== null}
        onClose={() => setOpen(null)}
        label={t('today.assign')}
        title={t('today.assignTitle')}
        closeLabel={t('app.close')}
      >
        {members.isPending ? <Skeleton height={48} /> : null}
        <List label={t('today.assignTitle')}>
          {(members.data ?? []).map((m) => (
            <ListItem
              key={m.id}
              title={m.name ?? t('today.unnamed')}
              supporting={t(`roles.${m.role}`)}
              onClick={() => open !== null && assign.mutate({ visit: open, member: m.id })}
            />
          ))}
        </List>
      </BottomSheet>
    </>
  );
}
